import { useAuthenticator } from '@aws-amplify/ui-react';
import { Button, Spinner } from '@nrg/design-system';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cognitoErrorAuthKey } from '@/auth/cognito-error-message';
import AuthFooterLink from '@/auth/components/AuthFooterLink';
import AuthScreen from '@/auth/components/AuthScreen';
import CodeField from '@/auth/components/CodeField';
import PasswordField from '@/auth/components/PasswordField';
import PasswordRules, { evaluatePassword } from '@/auth/components/PasswordRules';
import ResendButton from '@/auth/components/ResendButton';

export default function ConfirmResetPasswordScreen() {
  const { t } = useTranslation('auth');
  const { isPending, error, codeDeliveryDetails, submitForm, toSignIn, resendCode } =
    useAuthenticator((c) => [c.isPending, c.error, c.codeDeliveryDetails, c.route]);

  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const codeError = submitAttempted && code.length !== 6 ? t('errors.code_six_digits') : undefined;
  const { allPassed: pwOk } = evaluatePassword(password, t);
  const passwordError = submitAttempted && !pwOk ? t('errors.password_policy_failed') : undefined;
  const confirmError =
    submitAttempted && password !== confirmPassword ? t('errors.passwords_mismatch') : undefined;

  const cognitoKey = cognitoErrorAuthKey(error);
  const banner = cognitoKey ? t(cognitoKey) : null;

  const destination = codeDeliveryDetails?.Destination;
  const subhead = destination
    ? t('confirm_reset.subhead', { destination })
    : t('confirm_reset.subhead_no_destination');

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitAttempted(true);
    if (code.length !== 6 || !pwOk || password !== confirmPassword) return;
    submitForm({ confirmation_code: code, password, confirm_password: confirmPassword });
  };

  return (
    <AuthScreen
      heading={t('confirm_reset.heading')}
      subhead={subhead}
      error={banner}
      onSubmit={handleSubmit}
      footer={
        <>
          <ResendButton onResend={() => resendCode()} />
          <AuthFooterLink onClick={() => toSignIn()}>{t('common.back_to_sign_in')}</AuthFooterLink>
        </>
      }
    >
      <CodeField
        label={t('confirm_reset.code')}
        value={code}
        onChange={setCode}
        error={codeError}
        disabled={isPending}
        autoFocus
      />
      <PasswordField
        name="new-password"
        label={t('confirm_reset.new_password')}
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={passwordError}
        disabled={isPending}
      />
      <PasswordRules password={password} />
      <PasswordField
        name="confirm-password"
        label={t('confirm_reset.confirm_password')}
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={confirmError}
        disabled={isPending}
      />
      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        disabled={isPending}
        iconLeft={isPending ? <Spinner size="sm" /> : undefined}
      >
        {t('confirm_reset.submit')}
      </Button>
    </AuthScreen>
  );
}
